import { useState, useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Home, Image, Calendar, Users, MoreHorizontal, LogOut, Globe, Inbox, Package, FileText, BarChart2, ShieldCheck, Sun, Moon, Settings, type LucideIcon } from "lucide-react";
import type { Me } from "../lib/api";
import { useTheme } from "../lib/useTheme";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  match: RegExp;
};

const WEBSITE_MATCH = /^\/app\/(pages|portfolio)/;

const WEBSITE_SUB_ITEMS = [
  { to: "/app/pages", labelKey: "nav.pages" },
  { to: "/app/portfolio", labelKey: "nav.portfolio" },
] as const;

function navItems(t: (key: string) => string): NavItem[] {
  return [
    { to: "/app/dashboard", label: t("nav.home"), icon: Home, match: /^\/app\/(dashboard)?$/ },
    { to: "/app/analytics", label: t("nav.analytics"), icon: BarChart2, match: /^\/app\/analytics/ },
    { to: "/app/bookings", label: t("nav.bookings"), icon: Calendar, match: /^\/app\/bookings/ },
    { to: "/app/clients", label: t("nav.clients"), icon: Users, match: /^\/app\/clients/ },
    { to: "/app/galleries", label: t("nav.galleries"), icon: Image, match: /^\/app\/galleries/ },
    { to: "/app/invoices", label: t("nav.invoices"), icon: FileText, match: /^\/app\/invoices/ },
    { to: "/app/leads", label: t("nav.leads"), icon: Inbox, match: /^\/app\/leads/ },
    { to: "/app/packages", label: t("nav.packages"), icon: Package, match: /^\/app\/packages/ },
    { to: "/app/pages", label: t("nav.website"), icon: Globe, match: WEBSITE_MATCH },
  ];
}

function adminItem(t: (key: string) => string): NavItem {
  return { to: "/app/admin", label: t("nav.admin"), icon: ShieldCheck, match: /^\/app\/admin/ };
}

// Total slots in the bottom bar (including "More" when present).
const BOTTOM_MAX = 5;

interface ShellProps {
  route: string;
  user: Me;
  onLogout: () => void | Promise<void>;
  children: ReactNode;
  fullWidth?: boolean;
}

function avatarInitials(user: Me): string {
  if (user.display_name) {
    const parts = user.display_name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return user.email.slice(0, 2).toUpperCase();
}

export function Shell({ route, user, onLogout, children, fullWidth = false }: ShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const { pref, toggle } = useTheme();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!avatarOpen) return;
    function handleClick(e: MouseEvent) {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [avatarOpen]);

  const NAV = navItems(t);
  const ADMIN_ITEM = adminItem(t);

  const isAdmin = user.groups?.includes("professionals-admins") ?? false;

  function isActive(item: NavItem) {
    return item.match.test(route) || (item.to === "/app/dashboard" && (route === "/app" || route === "/app/"));
  }

  const visibleNav: NavItem[] = isAdmin ? [...NAV, ADMIN_ITEM] : NAV;

  // Don't render a "More" button just to hold a single item — promote it.
  const needsMore = visibleNav.length > BOTTOM_MAX;
  const bottomTabs = needsMore ? visibleNav.slice(0, BOTTOM_MAX - 1) : visibleNav;
  const moreItems = needsMore ? visibleNav.slice(BOTTOM_MAX - 1) : [];

  return (
    <div className="shell">
      <nav className="shell-top-nav">
        <a href="/app/dashboard" className="shell-brand" style={{ textDecoration: "none", color: "inherit" }}>
          Profes<span style={{ color: "var(--accent)" }}>sionals</span>
        </a>
        <div className="shell-nav-links">
          {NAV.map((item) => {
            const active = isActive(item);
            const isWebsite = item.to === "/app/pages" && item.match === WEBSITE_MATCH;
            return (
              <span key={item.to} style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
                <a
                  href={item.to}
                  className={"shell-nav-link" + (active ? " active" : "")}
                >
                  {item.label}
                </a>
                {isWebsite && active && (
                  <span style={{ display: "inline-flex", flexDirection: "column", paddingLeft: "0.75rem", gap: "0.1rem", marginTop: "0.1rem" }}>
                    {WEBSITE_SUB_ITEMS.map((sub) => (
                      <a
                        key={sub.to}
                        href={sub.to}
                        className={"shell-nav-link" + (route.startsWith(sub.to) ? " active" : "")}
                        style={{ fontSize: "0.78rem", paddingTop: "0.1rem", paddingBottom: "0.1rem" }}
                      >
                        {t(sub.labelKey)}
                      </a>
                    ))}
                  </span>
                )}
              </span>
            );
          })}
          {isAdmin && (
            <a
              href={ADMIN_ITEM.to}
              className={"shell-nav-link" + (ADMIN_ITEM.match.test(route) ? " active" : "")}
            >
              {ADMIN_ITEM.label}
            </a>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginLeft: "auto" }}>
          <button
            onClick={toggle}
            className="btn-ghost"
            style={{ padding: "0.4rem", lineHeight: 0, border: "none", background: "none" }}
            title={pref === "dark" ? t("nav.theme_light") : pref === "light" ? t("nav.theme_system") : t("nav.theme_dark")}
            aria-label="Toggle theme"
          >
            {pref === "dark" ? <Sun size={16} /> : pref === "light" ? <Settings size={16} /> : <Moon size={16} />}
          </button>
          <select
            value={i18n.language.slice(0, 2)}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            style={{
              fontSize: "0.8rem",
              padding: "0.2rem 0.4rem",
              borderRadius: "6px",
              border: "1px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
              width: "auto",
              cursor: "pointer",
            }}
            aria-label="Language"
          >
            <option value="en">EN</option>
            <option value="nl">NL</option>
            <option value="de">DE</option>
            <option value="fr">FR</option>
          </select>
          <div ref={avatarRef} className="avatar-wrap">
            <button
              className="avatar-btn"
              onClick={() => setAvatarOpen((o) => !o)}
              aria-label="Account menu"
              aria-expanded={avatarOpen}
              aria-haspopup="menu"
            >
              {avatarInitials(user)}
            </button>
            {avatarOpen && (
              <div className="avatar-dropdown" role="menu">
                <div className="avatar-dropdown-info">
                  <span className="avatar-dropdown-name">{user.display_name || user.email}</span>
                  <span className="avatar-dropdown-email">{user.email}</span>
                </div>
                <div className="avatar-dropdown-divider" />
                <button
                  className="avatar-dropdown-item"
                  role="menuitem"
                  onClick={() => { setAvatarOpen(false); onLogout(); }}
                >
                  <LogOut size={14} />
                  {t("nav.sign_out")}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className={"shell-main" + (fullWidth ? " shell-main--full" : "")}>{children}</main>

      <nav className="bottom-tab-bar" aria-label="Primary">
        {bottomTabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab);
          return (
            <a key={tab.to} href={tab.to} className={"bottom-tab" + (active ? " active" : "")}>
              <Icon size={20} />
              <span>{tab.label}</span>
            </a>
          );
        })}
        {needsMore && (
          <button className="bottom-tab" onClick={() => setMoreOpen(true)}>
            <MoreHorizontal size={20} />
            <span>{t("nav.more")}</span>
          </button>
        )}
      </nav>

      {moreOpen && (
        <>
          <div className="more-sheet-scrim" onClick={() => setMoreOpen(false)} />
          <div className="more-sheet" role="dialog" aria-label="More">
            <div className="more-sheet-handle" />
            <div style={{ marginBottom: "0.75rem", color: "var(--muted)", fontSize: "0.875rem" }}>
              Signed in as <strong style={{ color: "var(--text)" }}>{user.email}</strong>
            </div>
            {moreItems.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.to}
                  href={item.to}
                  onClick={() => setMoreOpen(false)}
                  className="more-sheet-item"
                  style={{ textDecoration: "none" }}
                >
                  <Icon size={18} />
                  {item.label}
                </a>
              );
            })}
            <button onClick={() => { setMoreOpen(false); onLogout(); }} className="more-sheet-item" style={{ background: "none", border: "none", color: "#ef4444", width: "100%", textAlign: "left" }}>
              <LogOut size={18} />
              {t("nav.sign_out")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
